import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth';
import { Select } from '@/components/ui/custom-select';

type EstablishmentOption = {
  value: string;
  label: string;
};

interface EstablishmentContextSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder: string;
  className?: string;
  isSearchable?: boolean;
  variant?: 'default' | 'topbar';
  includeAllOption?: boolean;
}

export const EstablishmentContextSelect = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  className,
  isSearchable = false,
  variant = 'default',
  includeAllOption = true,
}: EstablishmentContextSelectProps) => {
  const establishments = useAuthStore(
    (state) => state.user?.establishments ?? [],
  );

  const options = useMemo<EstablishmentOption[]>(
    () => {
      const establishmentOptions = establishments.map((item) => ({
        value: item.establishment_config_id,
        label: item.name,
      }));

      if (!includeAllOption || establishmentOptions.length <= 1) {
        return establishmentOptions;
      }

      return [
        {
          value: 'all',
          label: 'Todos os estabelecimentos',
        },
        ...establishmentOptions,
      ];
    },
    [establishments, includeAllOption],
  );

  const isTopbar = variant === 'topbar';
  const menuPortalTarget =
    typeof document !== 'undefined' && isTopbar ? document.body : undefined;

  return (
    <Select
      options={options}
      value={options.find((item) => item.value === value) ?? null}
      onChange={(option) => onChange(option?.value ?? '')}
      placeholder={placeholder}
      className={className}
      isSearchable={isSearchable}
      isDisabled={disabled}
      menuPortalTarget={menuPortalTarget}
      menuPosition={isTopbar ? 'fixed' : 'absolute'}
      classNamePrefix={
        isTopbar ? 'topbar-context-select' : 'react-select'
      }
      styles={
        isTopbar
          ? {
              control: (base, state) => ({
                ...base,
                minHeight: '1.9rem',
                height: '1.9rem',
                borderRadius: '0.3rem',
                borderColor: state.isFocused
                  ? 'hsl(var(--border))'
                  : 'hsl(var(--border))',
                backgroundColor: 'hsl(var(--background))',
                boxShadow: 'none',
                transition:
                  'background-color 150ms ease, border-color 150ms ease',
                cursor: 'pointer',
              }),
              valueContainer: (base) => ({
                ...base,
                paddingLeft: '0.875rem',
                paddingRight: '0.25rem',
                paddingTop: 0,
                paddingBottom: 0,
              }),
              placeholder: (base) => ({
                ...base,
                color: 'hsl(var(--card-foreground) / 0.5)',
                fontSize: '0.875rem',
              }),
              singleValue: (base) => ({
                ...base,
                color: 'hsl(var(--card-foreground) / 0.75)',
                fontSize: '0.875rem',
              }),
              indicatorsContainer: (base) => ({
                ...base,
                backgroundColor: 'transparent',
                paddingInline: '0.5rem',
              }),
              indicatorSeparator: () => ({
                display: 'none',
              }),
              dropdownIndicator: (base, state) => ({
                ...base,
                color: 'hsl(var(--card-foreground) / 0.5)',
                padding: 0,
                transform: state.selectProps.menuIsOpen
                  ? 'rotate(0deg)'
                  : 'rotate(-90deg)',
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999,
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }
          : undefined
      }
    />
  );
};
